import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { z } from 'zod';

import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import {
    type ApiError,
    type ApiResponseType,
    type Mode,
} from '@libraries/typescript/api_client';

const WORKSPACE_FILE_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/workspace-files/{file_path}';
export const WORKSPACE_FILE_PAGE_SIZE = 200;
const agentRunIdSchema = z.uuid();

export type AgentRunWorkspaceFilePage = ApiResponseType<
    typeof WORKSPACE_FILE_PATH,
    'get'
>;

type WorkspaceFilePageKey = readonly [
    typeof WORKSPACE_FILE_PATH,
    string,
    string,
    string,
    'csv' | 'jsonl' | undefined,
    string | undefined,
    number,
    Mode | null,
];

export function useAgentRunWorkspaceFileRows({
    organizationId,
    runId,
    filePath,
    format,
    eventId,
    mode,
    enabled = true,
}: {
    readonly organizationId: string;
    readonly runId: string | null;
    readonly filePath: string | null;
    readonly format?: 'csv' | 'jsonl';
    readonly eventId?: string;
    readonly mode?: Mode;
    readonly enabled?: boolean;
}): AgentRunWorkspaceFileRowsResult {
    const apiClient = getApiClient();
    const currentMode = useMode();
    const requestMode = mode ?? currentMode;
    const [pageIndex, setPageIndex] = useState(0);
    const [requestedPageIndex, setRequestedPageIndex] = useState<number | null>(
        null,
    );
    const getKey = (
        requestedPageIndex: number,
        previousPageData: AgentRunWorkspaceFilePage | null,
    ): WorkspaceFilePageKey | null => {
        const parsedRunId = agentRunIdSchema.safeParse(runId);
        if (!enabled || !parsedRunId.success || filePath === null) {
            return null;
        }
        if (previousPageData !== null && !previousPageData.has_more) {
            return null;
        }
        const offset =
            requestedPageIndex === 0
                ? 0
                : (previousPageData?.next_offset ?? null);
        if (offset === null) {
            return null;
        }
        return [
            WORKSPACE_FILE_PATH,
            organizationId,
            parsedRunId.data,
            filePath,
            format,
            eventId,
            offset,
            requestMode ?? null,
        ];
    };
    const { data, error, isLoading, size, setSize } = useSWRInfinite<
        AgentRunWorkspaceFilePage,
        ApiError
    >(
        getKey,
        (key: WorkspaceFilePageKey) => {
            const [
                path,
                orgId,
                requestedRunId,
                requestedFilePath,
                requestedFormat,
                requestedEventId,
                offset,
                requestMode,
            ] = key;
            return apiClient.get({
                url: path,
                pathParams: {
                    organization_id: orgId,
                    phoebe_agent_run_id: requestedRunId,
                    file_path: requestedFilePath,
                },
                queryParams: {
                    offset,
                    limit: WORKSPACE_FILE_PAGE_SIZE,
                    ...(requestedFormat === undefined
                        ? {}
                        : { format: requestedFormat }),
                    ...(requestedEventId === undefined
                        ? {}
                        : { event_id: requestedEventId }),
                },
                mode: requestMode ?? undefined,
                reason: 'agent-run-workspace-file-rows',
            });
        },
        {
            onError: () => setRequestedPageIndex(null),
            revalidateFirstPage: false,
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    );

    useEffect(() => {
        if (
            requestedPageIndex !== null &&
            data?.[requestedPageIndex] !== undefined
        ) {
            setPageIndex(requestedPageIndex);
            setRequestedPageIndex(null);
        }
    }, [data, requestedPageIndex]);

    const currentPage = data?.[pageIndex] ?? null;
    const rows = useMemo(
        () => data?.flatMap((page) => page.rows) ?? [],
        [data],
    );
    const loadNextPage = useCallback(() => {
        if (currentPage?.has_more !== true || requestedPageIndex !== null) {
            return;
        }
        const nextIndex = pageIndex + 1;
        if (data?.[nextIndex] !== undefined) {
            setPageIndex(nextIndex);
            return;
        }
        setRequestedPageIndex(nextIndex);
        void setSize(Math.max(size, nextIndex + 1));
    }, [
        currentPage?.has_more,
        data,
        pageIndex,
        requestedPageIndex,
        setSize,
        size,
    ]);
    const loadPreviousPage = useCallback(() => {
        if (pageIndex > 0 && requestedPageIndex === null) {
            setPageIndex(pageIndex - 1);
        }
    }, [pageIndex, requestedPageIndex]);

    return {
        rows,
        pageRows: currentPage?.rows ?? [],
        pageIndex,
        pageOffset: currentPage?.offset ?? 0,
        rowCount: data?.[0]?.row_count ?? null,
        error,
        pageError: currentPage === null ? undefined : error,
        isLoading,
        isLoadingMore: requestedPageIndex !== null,
        hasMore: currentPage?.has_more ?? false,
        hasPrevious: pageIndex > 0,
        loadNextPage,
        loadPreviousPage,
    };
}

export type AgentRunWorkspaceFileRowsResult = {
    readonly rows: ReadonlyArray<Record<string, unknown>>;
    readonly pageRows?: ReadonlyArray<Record<string, unknown>>;
    readonly pageIndex?: number;
    readonly pageOffset?: number;
    readonly rowCount: number | null;
    readonly error: ApiError | undefined;
    readonly pageError: ApiError | undefined;
    readonly isLoading: boolean;
    readonly isLoadingMore: boolean;
    readonly hasMore: boolean;
    readonly hasPrevious?: boolean;
    readonly loadNextPage: () => void;
    readonly loadPreviousPage: () => void;
};
